import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken } from "./api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // CRITICAL: skip /me check if returning from Google OAuth callback
      if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
        setLoading(false);
        return;
      }
      const t = getToken();
      if (!t) {
        setLoading(false);
        return;
      }
      const res = await api.me();
      setUser(res.data);
    } catch (e) {
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const signup = async (data) => {
    const res = await api.signup(data);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const login = async (data) => {
    const res = await api.login(data);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const googleSession = async (session_id) => {
    const res = await api.googleSession(session_id);
    if (res.data.token) setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try { await api.logout(); } catch { /* ignore */ }
    setToken(null);
    setUser(null);
  };

  const refresh = async () => {
    const res = await api.me();
    setUser(res.data);
    return res.data;
  };

  const updateProfile = async (data) => {
    const res = await api.updateProfile(data);
    setUser(res.data);
    return res.data;
  };

  const deleteAccount = async () => {
    await api.deleteAccount();
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user, loading,
      signup, login, googleSession, logout, refresh, updateProfile, deleteAccount,
      isPro: !!user?.is_pro,
      trialActive: !!user?.trial_active,
      trialDaysRemaining: user?.trial_days_remaining ?? 0,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
